import { ImageIcon, LoaderCircle, Save, Trash2, X } from "lucide-react";
import { useSelector } from "react-redux";
import userApi from "../../../../api/userApi";
import { useEffect, useRef, useState } from "react";
import { useNotification } from "../../../../hooks/useNotification";
import otherApi from "../../../../api/otherApi";

export default function AddStaffModal({
  isOpen,
  setIsOpen,
  editingStaff,
  setEditingStaff,
  getStaffs,
}) {
  const { addNotification } = useNotification();
  const auth = useSelector((state) => state.auth);
  const [data, setData] = useState({
    name: "",
    phone: "",
    username: "",
    password: "",
    avatar: "",
  });
  const [avatar, setAvatar] = useState(null);
  const avatarRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSaveClick = async () => {
    try {
      let res;
      if (!data.name.trim() || !data.phone.trim() || !data.password.trim()) {
        addNotification("Vui lòng điền đầy đủ thông tin", -1);
        return;
      }
      setIsLoading(true);
      if (avatarRef.current.files[0] && avatar) {
        const resPostImage = await otherApi.postImage(
          `avatar_${data.phone}`,
          avatarRef.current.files[0]
        );
        if (resPostImage.url) data.avatar = resPostImage.url;
      }
      if (editingStaff) {
        res = await userApi.updateById({
          ...data,
          _id: editingStaff._id,
        });
      } else {
        res = await userApi.create({
          ...data,
          accountId: auth.user.accountId,
          dateExpiration: auth.user.dateExpiration,
        });
      }
      if (res.status === 1) {
        setAvatar(null);
        await getStaffs();
        setIsLoading(false);
        addNotification("Lưu thành công", res.status);
        handleCloseClick();
      } else {
        setIsLoading(false);
        addNotification(res.message, res.status);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const handleCloseClick = () => {
    setIsOpen(false);
    setEditingStaff(null);
    setData({
      name: "",
      phone: "",
      username: "",
      password: "",
    });
  };

  const handleImageUpload = (e) => {
    if (e.target.files[0]) {
      const image = URL.createObjectURL(e.target.files[0]);
      setAvatar(image);
    }
  };

  const handleDeleteAvatar = () => {
    if (avatar) setAvatar(null);
    else setData({ ...data, avatar: "" });
  };

  useEffect(() => {
    if (editingStaff)
      setData({
        name: editingStaff.name,
        phone: editingStaff.phone,
        username: editingStaff.username,
        password: editingStaff.password,
        avatar: editingStaff.avatar,
      });
  }, [editingStaff]);

  return (
    <div>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                {editingStaff ? "Sửa nhân viên" : "Thêm nhân viên"}
              </h3>
              <button
                onClick={handleCloseClick}
                className="text-gray-400 hover:text-gray-500 transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="space-y-6">
                {/* Name Field */}
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Tên nhân viên
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={data?.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                    className="block w-full rounded-md border border-gray-300 px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>

                {/* Image upload */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">
                    Avatar
                  </span>
                  <div className="flex flex-wrap gap-4">
                    {(avatar || data.avatar) && (
                      <div className="relative group">
                        <img
                          src={avatar || data.avatar || "/placeholder.svg"}
                          alt={`Avatar`}
                          className="w-32 h-32 object-cover rounded-md border"
                        />
                        <button
                          onClick={handleDeleteAvatar}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <label className="border-2 border-dashed rounded-md p-4 w-32 h-32 flex flex-col items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer">
                      <ImageIcon className="w-6 h-6 mb-2" />
                      <span className="text-sm text-blue-500">Tải ảnh</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        ref={avatarRef}
                      />
                    </label>
                  </div>
                </div>

                {/* Phone Field */}
                <div className="space-y-2">
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Số điện thoại
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                      {auth.user.accountId}.
                    </span>
                    <input
                      id="phone"
                      type="tel"
                      value={data?.phone}
                      onChange={(e) =>
                        setData({
                          ...data,
                          phone: e.target.value.trim(),
                          username: `${
                            auth.user.accountId
                          }.${e.target.value.trim()}`,
                        })
                      }
                      className="block w-full rounded-none rounded-r-md border border-gray-300 px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Mật khẩu
                  </label>
                  <input
                    id="password"
                    value={data?.password}
                    onChange={(e) =>
                      setData({
                        ...data,
                        password: e.target.value.trim(),
                      })
                    }
                    className="block w-full rounded-md border border-gray-300 px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    className="inline-flex items-center gap-2 rounded-md bg-green-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 transition-colors duration-200"
                    onClick={handleSaveClick}
                    disabled={isLoading}
                  >
                    <Save className="h-4 w-4" />
                    Lưu
                    {isLoading && (
                      <LoaderCircle
                        className="animate-spin text-white mr-2"
                        width={20}
                        height={20}
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
